import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import {
  User as UserIcon,
  Building2,
  Plus,
  Loader2,
  CreditCard,
  Trash2,
  Pencil
} from "lucide-react";
import { toast } from "sonner";
import { authApi } from '@/services/authService';
import { useCustomAuth } from "@/contexts/CustomAuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import { Header } from '@/components/layout/header/Header';

const PaymentType = {
  BANK: 'bank',
  PIX: 'pix',
  PAYPAL: 'paypal'
};

interface PaymentMethodType {
  id_method: string;
  id_user: string;
  type: string;
  key_pix?: string;
  key_paypal?: string;
  bank_number?: string;
  account_bank_number?: string;
  name_user_account?: string;
  is_default: boolean;
  createdAt: string;
}

export default function PaymentMethods() {
  useLanguage();
  const { user } = useCustomAuth();
  const navigate = useNavigate();

  // Estados originais mantidos integralmente
  const [methods, setMethods] = useState<PaymentMethodType[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSettingDefault, setIsSettingDefault] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [selectedType, setSelectedType] = useState<string>(PaymentType.BANK);
  const [formData, setFormData] = useState({
    key_pix: '',
    key_paypal: '',
    bank_number: '',
    account_bank_number: '',
    name_user_account: '',
    is_default: false
  });

  useEffect(() => {
    if (user) {
      const allowedLevels = ['seller', 'manager'];
      if (!allowedLevels.includes(user.nivel)) {
        navigate('/dashboard');
      } else {
        fetchPaymentMethods();
      }
    }
  }, [user, navigate]);

  const fetchPaymentMethods = async () => {
    try {
      setLoading(true);
      const response = await authApi.get(`/stripe/afiliate/finance/methods/payment-methods/user/${user?.id_user}`);
      if (response.status === 200) {
        const data = response.data || [];

        const sortedMethods = data.sort((m1: PaymentMethodType, m2: PaymentMethodType) => {
          if (m1.is_default && !m2.is_default) return -1;
          if (!m1.is_default && m2.is_default) return 1;
          return 0;
        });

        setMethods(sortedMethods);
      }
    } catch (err: any) {
      console.error(err);
      toast.error(translateInterfaceText("Erro ao carregar métodos de pagamento."));
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefault = async (id_method: string) => {
    try {
      setIsSettingDefault(id_method);
      const response = await authApi.patch(`/stripe/afiliate/finance/methods/payment-methods/${id_method}/set-default`);

      if (response.status === 200) {
        toast.success(translateInterfaceText("Método de pagamento definido como padrão!"));
        await fetchPaymentMethods();
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.error || translateInterfaceText("Erro ao definir método como padrão."));
    } finally {
      setIsSettingDefault(null);
    }
  };

  const handleCreateMethod = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedType === PaymentType.BANK && (!formData.bank_number || !formData.account_bank_number || !formData.name_user_account)) {
      toast.error(translateInterfaceText("Preencha todos os campos da conta bancária."));
      return;
    }
    if (selectedType === PaymentType.PIX && !formData.key_pix) {
      toast.error(translateInterfaceText("Por favor, preencha a chave PIX."));
      return;
    }
    if (selectedType === PaymentType.PAYPAL && !formData.key_paypal) {
      toast.error(translateInterfaceText("Por favor, preencha o e-mail do PayPal."));
      return;
    }

    try {
      setIsSaving(true);

      const payload = {
        id_user: user.id_user,
        type: selectedType,
        ...formData
      };

      const response = await authApi.post('/stripe/afiliate/finance/methods/payment-methods', payload);

      if (response.status === 201) {
        toast.success(translateInterfaceText("Método de pagamento adicionado com sucesso!"));
        setIsModalOpen(false);
        setFormData({
          key_pix: '',
          key_paypal: '',
          bank_number: '',
          account_bank_number: '',
          name_user_account: '',
          is_default: false
        });
        await fetchPaymentMethods();
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.error || translateInterfaceText("Erro ao salvar método de pagamento."));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteMethod = async (id_method: string) => {
    if (!confirm(translateInterfaceText("Tem certeza que deseja remover este método de pagamento?"))) return;

    try {
      setIsDeleting(id_method);
      const response = await authApi.delete(`/stripe/afiliate/finance/methods/payment-methods/${id_method}`);

      if (response.status === 200) {
        toast.success(translateInterfaceText("Método de pagamento removido."));
        await fetchPaymentMethods();
      }
    } catch (err: any) {
      console.error(err);
      toast.error(translateInterfaceText("Erro ao deletar método de pagamento."));
    } finally {
      setIsDeleting(null);
    }
  };

  if (loading) return <div className="h-screen flex items-center justify-center"><Loader2 className="animate-spin text-primary" /></div>;

  return (
    <Layout>
      <Header title={translateInterfaceText("Configurações de Recebimento")} />
      <div className="p-8 max-w-[1600px] mx-auto space-y-6 font-sans antialiased text-slate-800">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">

          {/* CARD ESQUERDO: Perfil Comercial (Imagem 2) */}
          <div className="lg:col-span-4">
            <Card className="border border-slate-200 shadow-sm bg-white rounded-xl p-6">
              <div className="space-y-1 mb-6">
                <h3 className="text-base font-bold text-black">{translateInterfaceText("Perfil Comercial")}</h3>
                <p className="text-xs text-slate-400 font-normal leading-normal">
                  {translateInterfaceText("Dados usados para validação da sua conta de recebimento.")}</p>
              </div>

              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">{translateInterfaceText("Nome do Usuário")}</Label>
                  <Input
                    value={user?.nome || ''}
                    disabled
                    className="h-11 text-sm border border-slate-200 bg-white text-slate-400 rounded-lg placeholder:text-slate-300 focus-visible:ring-0 disabled:opacity-100"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">{translateInterfaceText("E-mail Cadastrado")}</Label>
                  <Input
                    value={user?.email || ''}
                    disabled
                    className="h-11 text-sm border border-slate-200 bg-white text-slate-400 rounded-lg placeholder:text-slate-300 focus-visible:ring-0 disabled:opacity-100"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">{translateInterfaceText("Cpf Cadastrado")}</Label>
                  <Input
                    value={user?.cpf || ''}
                    disabled
                    className="h-11 text-sm border border-slate-200 bg-white text-slate-400 rounded-lg placeholder:text-slate-300 focus-visible:ring-0 disabled:opacity-100"
                  />
                </div>
              </div>
            </Card>
          </div>

          {/* ÁREA DIREITA: Tabela de Bancos Vinculados (Imagem 2) */}
          <div className="lg:col-span-8">
            <Card className="border border-slate-200 shadow-sm bg-white rounded-xl overflow-hidden">

              {/* Header da Tabela */}
              <div className="flex flex-row items-center justify-between p-6 bg-white border-b border-slate-100">
                <div className="space-y-1">
                  <h3 className="text-base font-bold text-black">{translateInterfaceText("Contas vinculadas")}</h3>
                  <p className="text-xs text-slate-400 font-normal">
                    {translateInterfaceText("Adicione uma conta para receber seus saques.")}</p>
                </div>

                {/* MODAL ORIGINAL MANTIDO E ESTILIZADO (Imagem 3) */}
                <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" className="font-semibold text-blue-600 border-blue-200 bg-white hover:bg-blue-50/50 h-10 px-4 rounded-xl text-xs flex items-center gap-1.5 shadow-none transition-colors">
                      <Plus className="h-4 w-4 stroke-[2.5]" /> {" "}{translateInterfaceText("Vincular")}</Button>
                  </DialogTrigger>

                  <DialogContent className="sm:max-w-[420px] p-6 rounded-2xl border border-slate-100 shadow-2xl bg-white gap-0">
                    <DialogHeader className="space-y-1 text-left pb-3">
                      <DialogTitle className="text-xl font-bold text-slate-900 tracking-tight">{translateInterfaceText("Novo Método de Pagamento")}</DialogTitle>
                      <DialogDescription className="text-sm text-slate-400 font-normal leading-normal">
                        {translateInterfaceText("Escolha o tipo e preencha com atenção os dados para repasse.")}</DialogDescription>
                    </DialogHeader>

                    {/* Formulário, Ordem dos Inputs e Lógica Originais */}
                    <form onSubmit={handleCreateMethod} className="space-y-4 pt-2">
                      <div className="space-y-1.5">
                        <Label className="text-sm font-bold text-slate-700">{translateInterfaceText("Tipo de Recebimento")}</Label>
                        <Select value={selectedType} onValueChange={(v) => setSelectedType(v)}>
                          <SelectTrigger className="h-12 text-sm border-2 border-blue-600 rounded-xl bg-white text-slate-700 focus:ring-0 focus:border-blue-600 font-medium px-4">
                            <SelectValue placeholder={translateInterfaceText("Selecione a modalidade")} />
                          </SelectTrigger>
                          <SelectContent className="rounded-xl border-slate-200 shadow-lg">
                            <SelectItem value={PaymentType.BANK}>{translateInterfaceText("Conta Bancária Tradicional")}</SelectItem>
                            <SelectItem value={PaymentType.PIX}>{translateInterfaceText("Chave PIX")}</SelectItem>
                            <SelectItem value={PaymentType.PAYPAL}>{translateInterfaceText("PayPal (E-mail)")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {selectedType === PaymentType.BANK && (
                        <div className="space-y-4 animate-in fade-in duration-200">
                          <div className="space-y-1.5">
                            <Label className="text-sm font-bold text-slate-700">{translateInterfaceText("Nome do Titular da Conta")}</Label>
                            <Input
                              placeholder={translateInterfaceText("Igual no banco")}
                              value={formData.name_user_account}
                              onChange={e => setFormData({ ...formData, name_user_account: e.target.value })}
                              className="h-12 px-4 text-sm border border-slate-200 rounded-xl bg-white text-slate-700 placeholder:text-slate-400 focus-visible:ring-1 focus-visible:ring-blue-500"
                            />
                          </div>

                          <div className="space-y-1.5">
                            <Label className="text-sm font-bold text-slate-700">{translateInterfaceText("Instituição Bancária (Cód. Banco)")}</Label>
                            <Select
                              value={formData.bank_number}
                              onValueChange={(v) => setFormData({ ...formData, bank_number: v })}
                            >
                              <SelectTrigger className="h-12 text-sm border border-slate-200 rounded-xl bg-white text-slate-700 focus:ring-1 focus:ring-blue-500">
                                <SelectValue placeholder={translateInterfaceText("Selecione o seu banco")} />
                              </SelectTrigger>
                              <SelectContent className="max-h-60 overflow-y-auto rounded-xl border-slate-200 shadow-lg">
                                <SelectItem value="001">{translateInterfaceText("001 - Banco do Brasil S.A.")}</SelectItem>
                                <SelectItem value="237">{translateInterfaceText("237 - Banco Bradesco S.A.")}</SelectItem>
                                <SelectItem value="341">{translateInterfaceText("341 - Itaú Unibanco S.A.")}</SelectItem>
                                <SelectItem value="033">{translateInterfaceText("033 - Banco Santander (Brasil) S.A.")}</SelectItem>
                                <SelectItem value="104">{translateInterfaceText("104 - Caixa Econômica Federal")}</SelectItem>
                                <SelectItem value="399">{translateInterfaceText("399 - Kirton Bank S.A. (Banco Múltiplo)")}</SelectItem>
                                <SelectItem value="422">{translateInterfaceText("422 - Banco Safra S.A.")}</SelectItem>
                                <SelectItem value="745">{translateInterfaceText("745 - Banco Citibank S.A.")}</SelectItem>
                                <SelectItem value="260">{translateInterfaceText("260 - Nu Pagamentos S.A. (Nubank)")}</SelectItem>
                                <SelectItem value="077">{translateInterfaceText("077 - Banco Inter S.A.")}</SelectItem>
                                <SelectItem value="536">{translateInterfaceText("536 - Neon Pagamentos S.A. (Banco Neon)")}</SelectItem>
                                <SelectItem value="756">{translateInterfaceText("756 - Banco Ourinvest S.A. (Nomad)")}</SelectItem>
                                <SelectItem value="336">{translateInterfaceText("336 - Banco C6 S.A. (C6 Bank)")}</SelectItem>
                                <SelectItem value="212">{translateInterfaceText("212 - Banco Original S.A.")}</SelectItem>
                                <SelectItem value="290">{translateInterfaceText("290 - Pagseguro Internet S.A. (PagBank)")}</SelectItem>
                                <SelectItem value="323">{translateInterfaceText("323 - Mercado Pago Caixa Eletrônica do MP Ltda.")}</SelectItem>
                                <SelectItem value="197">{translateInterfaceText("197 - Stone Co. (Stone Pagamentos)")}</SelectItem>
                                <SelectItem value="403">{translateInterfaceText("403 - Cora Sociedade de Crédito Direto S.A.")}</SelectItem>
                                <SelectItem value="389">{translateInterfaceText("389 - Banco Mercantil do Brasil S.A.")}</SelectItem>
                                <SelectItem value="655">{translateInterfaceText("655 - Banco Votorantim S.A. (Banco BV)")}</SelectItem>
                                <SelectItem value="041">{translateInterfaceText("041 - Banco do Estado do Rio Grande do Sul S.A. (Banrisul)")}</SelectItem>
                                <SelectItem value="070">{translateInterfaceText("070 - Banco de Brasília S.A. (BRB)")}</SelectItem>
                                <SelectItem value="004">{translateInterfaceText("004 - Banco do Nordeste do Brasil S.A.")}</SelectItem>
                                <SelectItem value="021">{translateInterfaceText("021 - BANESTES S.A. Banco do Estado do Espírito Santo")}</SelectItem>
                                <SelectItem value="748">{translateInterfaceText("748 - Banco Cooperativo Sicredi S.A.")}</SelectItem>
                                <SelectItem value="756_coop">{translateInterfaceText("756 - Banco Cooperativo do Brasil S.A. (Sicoob)")}</SelectItem>
                                <SelectItem value="136">{translateInterfaceText("136 - Confederação Nacional das Cooperativas Unicred")}</SelectItem>
                                <SelectItem value="637">{translateInterfaceText("637 - Banco Sofisa S.A.")}</SelectItem>
                                <SelectItem value="623">{translateInterfaceText("623 - Banco Pan S.A.")}</SelectItem>
                                <SelectItem value="208">{translateInterfaceText("208 - Banco BTG Pactual S.A.")}</SelectItem>
                                <SelectItem value="085">{translateInterfaceText("085 - Cooperativa Central de Crédito Ailos")}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-1.5">
                            <Label className="text-sm font-bold text-slate-700">{translateInterfaceText("Número da Conta com DV")}</Label>
                            <Input
                              placeholder={translateInterfaceText("Ex: 12345-6")}
                              value={formData.account_bank_number}
                              onChange={e => setFormData({ ...formData, account_bank_number: e.target.value })}
                              className="h-12 px-4 text-sm border border-slate-200 rounded-xl bg-white text-slate-700 placeholder:text-slate-400 focus-visible:ring-1 focus-visible:ring-blue-500"
                            />
                          </div>
                        </div>
                      )}

                      {selectedType === PaymentType.PIX && (
                        <div className="space-y-1.5 animate-in fade-in duration-200">
                          <Label className="text-sm font-bold text-slate-700">{translateInterfaceText("Chave PIX (CPF, E-mail, Celular ou Aleatória)")}</Label>
                          <Input
                            placeholder={translateInterfaceText("Insira sua chave PIX aqui")}
                            value={formData.key_pix}
                            onChange={e => setFormData({ ...formData, key_pix: e.target.value })}
                            className="h-12 px-4 text-sm border border-slate-200 rounded-xl bg-white text-slate-700 placeholder:text-slate-400 focus-visible:ring-1 focus-visible:ring-blue-500"
                          />
                        </div>
                      )}

                      {selectedType === PaymentType.PAYPAL && (
                        <div className="space-y-1.5 animate-in fade-in duration-200">
                          <Label className="text-sm font-bold text-slate-700">{translateInterfaceText("E-mail da Conta PayPal")}</Label>
                          <Input
                            type="email"
                            placeholder="exemplo@paypal.com"
                            value={formData.key_paypal}
                            onChange={e => setFormData({ ...formData, key_paypal: e.target.value })}
                            className="h-12 px-4 text-sm border border-slate-200 rounded-xl bg-white text-slate-700 placeholder:text-slate-400 focus-visible:ring-1 focus-visible:ring-blue-500"
                          />
                        </div>
                      )}

                      <div className="flex items-center space-x-2 pt-1">
                        <input
                          type="checkbox"
                          id="is_default"
                          checked={formData.is_default}
                          onChange={e => setFormData({ ...formData, is_default: e.target.checked })}
                          className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                        />
                        <label htmlFor="is_default" className="text-xs font-semibold text-slate-500 select-none cursor-pointer">
                          {translateInterfaceText("Definir como padrão/principal")}</label>
                      </div>

                      <Button
                        type="submit"
                        className="w-full h-12 font-bold bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm mt-2 flex items-center justify-center transition-colors shadow-none tracking-wide"
                        disabled={isSaving}
                      >
                        {isSaving ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <>{translateInterfaceText("Salvar Método de Recebimento")}</>
                        )}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Tabela de Elementos Tabulares Corporativos (Imagem 2) */}
              <div className="w-full overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-slate-100 text-[11px] font-bold tracking-wider text-slate-400 uppercase bg-[#fafafa]">
                      <th className="py-4 px-6 font-bold">{translateInterfaceText("Banco")}</th>
                      <th className="py-4 px-4 font-bold">{translateInterfaceText("Agência")}</th>
                      <th className="py-4 px-4 font-bold">{translateInterfaceText("Conta")}</th>
                      <th className="py-4 px-4 font-bold">{translateInterfaceText("Tipo")}</th>
                      <th className="py-4 px-4 font-bold">{translateInterfaceText("Chave / Identificador")}</th>
                      <th className="py-4 px-4 font-bold">{translateInterfaceText("Status")}</th>
                      <th className="py-4 px-6 text-right font-bold">{translateInterfaceText("Ações")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {methods.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="py-12 text-center text-xs text-slate-400 font-medium">
                          <div className="flex flex-col items-center justify-center gap-2">
                            <CreditCard className="h-6 w-6 text-slate-300" />
                            <span>{translateInterfaceText("Nenhum método de pagamento cadastrado.")}</span>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      methods.map((method) => {
                        const labelBanco = method.type === PaymentType.BANK ? (method.bank_number ? `Banco ${method.bank_number}` : translateInterfaceText("Conta Bancária")) : method.type.toUpperCase();
                        const contaFormatada = method.account_bank_number || '--';
                        const identificadorChave = method.type === PaymentType.BANK
                          ? (method.name_user_account ? method.name_user_account.toUpperCase() : '--')
                          : (method.key_pix || method.key_paypal || '--');

                        return (
                          <tr key={method.id_method} className="hover:bg-slate-50/50 transition-colors text-xs font-medium text-slate-700">

                            {/* BANCO */}
                            <td className="py-4 px-6 whitespace-nowrap">
                              <div className="flex items-center gap-2.5">
                                <div className="h-7 w-7 rounded-full bg-blue-100 text-blue-600 text-[10px] font-bold flex items-center justify-center shrink-0">
                                  {method.type === PaymentType.BANK ? <Building2 className="h-3.5 w-3.5" /> : <CreditCard className="h-3.5 w-3.5" />}
                                </div>
                                <span className="font-bold text-slate-900">{labelBanco}</span>
                                {method.is_default && (
                                  <Badge className="bg-blue-50 hover:bg-blue-50 text-blue-600 shadow-none text-[10px] font-bold px-2 py-0 h-5 rounded-full flex items-center gap-1">
                                    {translateInterfaceText("👑 Principal")}</Badge>
                                )}
                              </div>
                            </td>

                            {/* AGÊNCIA (Preenchimento simulado conforme layout limpo da foto 2) */}
                            <td className="py-4 px-4 text-slate-600 whitespace-nowrap">
                              {method.type === PaymentType.BANK ? '0001' : '--'}
                            </td>

                            {/* CONTA */}
                            <td className="py-4 px-4 text-slate-600 whitespace-nowrap">
                              {contaFormatada}
                            </td>

                            {/* TIPO */}
                            <td className="py-4 px-4 text-slate-500 whitespace-nowrap capitalize">
                              {method.type === PaymentType.BANK ? translateInterfaceText("Conta corrente") : method.type}
                            </td>

                            {/* CHAVE / IDENTIFICADOR */}
                            <td className="py-4 px-4 whitespace-nowrap">
                              <span className="text-slate-800 font-medium truncate max-w-[180px] block">{identificadorChave}</span>
                            </td>

                            {/* STATUS */}
                            <td className="py-4 px-4 whitespace-nowrap">
                              <Badge className="bg-emerald-50 text-emerald-600 border border-emerald-100 shadow-none text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 w-fit">
                                <span className="h-1 w-1 rounded-full bg-emerald-500" /> {" "}{translateInterfaceText("Ativo")}</Badge>
                            </td>

                            {/* AÇÕES */}
                            <td className="py-4 px-6 text-right whitespace-nowrap">
                              <div className="flex items-center justify-end gap-2">
                                {!method.is_default && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleSetDefault(method.id_method)}
                                    disabled={isSettingDefault !== null}
                                    className="h-8 text-[11px] font-bold text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg px-2 shadow-none"
                                  >
                                    {isSettingDefault === method.id_method ? (
                                      <Loader2 className="h-3 w-3 animate-spin" />
                                    ) : (
                                      translateInterfaceText("Tornar Principal")
                                    )}
                                  </Button>
                                )}

                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8 text-slate-300 hover:text-slate-500 hover:bg-slate-100 rounded-lg"
                                  disabled
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>

                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => handleDeleteMethod(method.id_method)}
                                  disabled={isDeleting !== null}
                                  className="h-8 w-8 text-slate-300 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                >
                                  {isDeleting === method.id_method ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-4 w-4" />
                                  )}
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>

        </div>
      </div>
    </Layout>
  );
}